/**
 * Staff actions on one domain: name servers, DNSSEC, transfer lock, domain hold (clientHold),
 * transfer code, renewal, restore, registrant change (with the new registrant contact created
 * ahead), deletion, the contacts held at the registry and the e-mails sent by hand. Each change
 * asks for a reason (kept in the audit log) and goes to the registry through the same process as
 * a customer's request.
 */

import { Ban, CalendarPlus, ChevronDown, Contact, KeyRound, Lock, LockKeyhole, LockOpen, Mail, MoreHorizontal, RefreshCw, Server, ShieldCheck, UserCog } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, type DomainDetail } from '../../api';
import type { ContactData, DomainContact, DsInput, NameserverInput, RegistrantContact, RegistryContactRow, RenewQuote, StaffEmailKey, TransferCodeResult } from '../../api/areas/core';
import { Callout, Field } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { NotifyCustomerChoice, NotifyCustomerField } from '../../components/NotifyCustomer';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { addressLine } from '../../lib/address';
import { formatDateTime, formatMoney } from '../../lib/format';
import { transferLockHeldUntil } from '../../lib/transferLock';
import { CONSENT_METHODS, CONSENT_SIDES, consentMode, type ConsentSide, type StaffConsentMethod } from '../../lib/registrantConsent';
import { useApi } from '../../lib/useApi';
import { NameChangeForm } from '../services/NameChangeForm';
import { RoleBadge } from './refs';
import { domainTabUrl } from './tabs/moved';

type Action =
  | 'nameservers'
  | 'dnssec'
  | 'lock'
  | 'updateLock'
  | 'hold'
  | 'transferCode'
  | 'renew'
  | 'restore'
  | 'registrant'
  | 'nameChange'
  | 'registryContacts'
  | 'email'
  | 'delete';

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

function Reason({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('domains');
  return (
    <>
      <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} maxLength={500} required />
      </Field>
      <NotifyCustomerField />
    </>
  );
}

function Done({ children }: { children: ReactNode }) {
  return <Callout tone="ok">{children}</Callout>;
}

function Footer({ busy, disabled, submit, onClose, danger }: { busy: boolean; disabled?: boolean; submit: string; onClose: () => void; danger?: boolean }) {
  const { t } = useTranslation('domains');
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        {t('actions.close')}
      </Button>
      <Button type="submit" variant={danger ? 'destructive' : 'default'} disabled={busy || disabled}>
        {submit}
      </Button>
    </DialogFooter>
  );
}

function ResultText({ status }: { status: string }) {
  const { t } = useTranslation('domains');
  return <Done>{status === 'APPLIED' ? t('actions.applied') : t('actions.pending')}</Done>;
}

/** Addresses typed for one name server (commas or spaces between them). */
const addressList = (s: string | undefined) => (s ?? '').split(/[\s,;]+/).map((a) => a.trim()).filter(Boolean);
/** A typed host in the ASCII (punycode) form the core stores and compares; as typed when already ASCII or not a host. */
const asciiHost = (h: string) => {
  if (![...h].some((c) => c.charCodeAt(0) > 127)) return h;
  try {
    return new URL(`http://${h}`).hostname;
  } catch {
    return h;
  }
};
/** Entries of an address box that are plainly not of its kind (a light hint; the core decides). */
const notOfKind = (s: string | undefined, kind: 'v4' | 'v6') =>
  addressList(s).filter((a) => (kind === 'v4' ? !/^\d{1,3}(\.\d{1,3}){3}$/.test(a) : !a.includes(':') || a.includes('.')));

function DelegationError({ error, onRetry, onClose }: { error: unknown; onRetry: () => void; onClose: () => void }) {
  const { t } = useTranslation('domains');
  return (
    <div className="grid gap-4">
      <DataError error={error} onRetry={onRetry} />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>{t('actions.close')}</Button>
      </DialogFooter>
    </div>
  );
}

function NameserversForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const current = useApi((signal) => api.getDomainDelegation(d.domain.ascii_name, { signal }), [d.domain.ascii_name]);
  const [text, setText] = useState<string | null>(null);
  const [glue, setGlue] = useState<Record<string, { v4: string; v6: string }> | null>(null);
  const [reason, setReason] = useState('');
  const w = useWrite((b: { nameservers: NameserverInput[]; reason: string }, opts) => api.setDomainNameservers(d.domain.id, b, opts));
  if (current.status === 'loading') return <Loading rows={2} />;
  if (current.status === 'error') return <DelegationError error={current.error} onRetry={current.reload} onClose={onClose} />;
  const value = text ?? current.data.nameservers.map((n) => n.host).join('\n');
  const addresses =
    glue ??
    Object.fromEntries(
      current.data.nameservers.map((n) => [n.host, { v4: n.addresses.filter((a) => !a.includes(':')).join(', '), v6: n.addresses.filter((a) => a.includes(':')).join(', ') }]),
    );
  const hosts = value
    .split(/[\s,]+/)
    .map((h) => asciiHost(h.trim().toLowerCase().replace(/\.$/, '')))
    .filter(Boolean);
  // A name server under the domain itself, its apex included (the core's rule), goes to the registry
  // with its IPv4/IPv6 addresses (glue, EPP hostAttr/hostAddr); any other by name only. The core
  // checks syntax, kind, duplicates and counts; here only that an address is there and each box
  // holds addresses of its kind.
  const domain = d.domain.ascii_name.toLowerCase();
  const under = (h: string) => h === domain || h.endsWith(`.${domain}`);
  const unique = hosts.filter((h, i) => hosts.indexOf(h) === i);
  const inner = unique.filter(under);
  const outer = unique.filter((h) => !under(h));
  const ips = (h: string) => [...addressList(addresses[h]?.v4), ...addressList(addresses[h]?.v6)];
  const setIp = (h: string, kind: 'v4' | 'v6', v: string) => setGlue({ ...addresses, [h]: { v4: '', v6: '', ...addresses[h], [kind]: v } });
  const missingGlue = inner.some((h) => ips(h).length === 0);
  const wrongKind = inner.some((h) => notOfKind(addresses[h]?.v4, 'v4').length > 0 || notOfKind(addresses[h]?.v6, 'v6').length > 0);
  const kindHint = (h: string, kind: 'v4' | 'v6') => {
    const bad = notOfKind(addresses[h]?.[kind], kind);
    return bad.length ? <span className="text-destructive">{t(kind === 'v4' ? 'actions.ns.notIpv4' : 'actions.ns.notIpv6', { list: bad.join(', ') })}</span> : undefined;
  };
  const nameservers: NameserverInput[] = hosts.map((h) => (under(h) ? { host: h, addresses: ips(h) } : h));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ nameservers, reason }).then((r) => r && onChanged());
      }}
    >
      <Field label={t('actions.ns.label')} hint={t('actions.ns.hint', { count: hosts.length })}>
        <Textarea value={value} onChange={(e) => setText(e.target.value)} rows={5} className="font-mono" />
      </Field>
      {unique.length > 0 ? (
        <fieldset className="grid gap-3 rounded-md border p-3">
          <legend className="px-1 text-sm font-medium">{t('actions.ns.glueTitle')}</legend>
          <p className="text-xs text-muted-foreground">{t('actions.ns.glueHint', { domain })}</p>
          {inner.map((h) => (
            <Field key={h} group label={<span className="font-mono">{h}</span>}>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label={t('actions.ns.ipv4')} hint={kindHint(h, 'v4')}>
                  <Input className="font-mono" value={addresses[h]?.v4 ?? ''} placeholder={t('actions.ns.ipv4Placeholder')} onChange={(e) => setIp(h, 'v4', e.target.value)} />
                </Field>
                <Field label={t('actions.ns.ipv6')} hint={kindHint(h, 'v6')}>
                  <Input className="font-mono" value={addresses[h]?.v6 ?? ''} placeholder={t('actions.ns.ipv6Placeholder')} onChange={(e) => setIp(h, 'v6', e.target.value)} />
                </Field>
              </div>
            </Field>
          ))}
          {outer.length > 0 ? <p className="text-xs text-muted-foreground">{t('actions.ns.external', { list: outer.join(', ') })}</p> : null}
          {missingGlue ? <p className="text-xs text-destructive">{t('actions.ns.glueMissing')}</p> : null}
        </fieldset>
      ) : null}
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} disabled={hosts.length < 2 || missingGlue || wrongKind} submit={t('actions.ns.submit')} onClose={onClose} />
    </form>
  );
}

function DnssecForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const current = useApi((signal) => api.getDomainDelegation(d.domain.ascii_name, { signal }), [d.domain.ascii_name]);
  const [rows, setRows] = useState<DsInput[] | null>(null);
  const [reason, setReason] = useState('');
  const w = useWrite((b: { ds: DsInput[]; reason: string }, opts) => api.setDomainDnssec(d.domain.id, b, opts));
  if (current.status === 'loading') return <Loading rows={2} />;
  if (current.status === 'error') return <DelegationError error={current.error} onRetry={current.reload} onClose={onClose} />;
  const initial: DsInput[] = current.data.ds_records.map((r) => ({
    key_tag: r.key_tag,
    algorithm: r.algorithm,
    digest_type: r.digest_type,
    digest: r.digest,
    key_data: r.public_key && r.key_flags !== null && r.key_protocol !== null && r.key_algorithm !== null ? { flags: r.key_flags, protocol: r.key_protocol, algorithm: r.key_algorithm, public_key: r.public_key } : null,
  }));
  const ds = rows ?? initial;
  const set = (i: number, patch: Partial<DsInput>) => setRows(ds.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const num = (v: string) => Number(v.replace(/\D/g, '')) || 0;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ ds, reason }).then((r) => r && onChanged());
      }}
    >
      {current.data.supports_dnssec === false ? <Callout tone="warn">{t('actions.ds.unsupported')}</Callout> : null}
      {ds.length === 0 ? <p className="text-sm text-muted-foreground">{t('actions.ds.none')}</p> : null}
      {ds.map((r, i) => (
        <fieldset key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-4">
          <Field label={t('actions.ds.keyTag')}>
            <Input inputMode="numeric" value={r.key_tag || ''} onChange={(e) => set(i, { key_tag: num(e.target.value) })} />
          </Field>
          <Field label={t('actions.ds.algorithm')}>
            <Input inputMode="numeric" value={r.algorithm || ''} onChange={(e) => set(i, { algorithm: num(e.target.value) })} />
          </Field>
          <Field label={t('actions.ds.digestType')}>
            <Input inputMode="numeric" value={r.digest_type || ''} onChange={(e) => set(i, { digest_type: num(e.target.value) })} />
          </Field>
          <div className="flex items-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setRows(ds.filter((_, j) => j !== i))}>
              {t('actions.ds.remove')}
            </Button>
          </div>
          <Field label={t('actions.ds.digest')} className="sm:col-span-4">
            <Input className="font-mono" value={r.digest} onChange={(e) => set(i, { digest: e.target.value.trim() })} />
          </Field>
          <Field label={t('actions.ds.publicKey')} hint={t('actions.ds.publicKeyHint')} className="sm:col-span-4">
            <Textarea
              className="font-mono"
              rows={2}
              value={r.key_data?.public_key ?? ''}
              onChange={(e) => set(i, { key_data: e.target.value.trim() ? { flags: r.key_data?.flags ?? 257, protocol: 3, algorithm: r.algorithm, public_key: e.target.value } : null })}
            />
          </Field>
        </fieldset>
      ))}
      <div>
        <Button type="button" variant="outline" size="sm" onClick={() => setRows([...ds, { key_tag: 0, algorithm: 13, digest_type: 2, digest: '', key_data: null }])}>
          {t('actions.ds.add')}
        </Button>
      </div>
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} submit={ds.length ? t('actions.ds.submit') : t('actions.ds.turnOff')} onClose={onClose} />
    </form>
  );
}

function LockForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const locked = d.registry_statuses.some((s) => s.status === 'clientTransferProhibited');
  // The transfer policy's lock keeps it locked until its end: unlocking would only be refused.
  const [now] = useState(() => Date.now());
  const held = locked ? transferLockHeldUntil(d.locks, now) : undefined;
  const [reason, setReason] = useState('');
  const w = useWrite((b: { locked: boolean; reason: string }, opts) => api.setDomainTransferLock(d.domain.id, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ locked: !locked, reason }).then((r) => r && onChanged());
      }}
    >
      <p className="text-sm">{locked ? t('actions.lock.nowLocked') : t('actions.lock.nowUnlocked')}</p>
      {held !== undefined ? (
        <Callout tone="warn">{held ? t('actions.lock.heldUntil', { until: formatDateTime(held) }) : t('actions.lock.held')}</Callout>
      ) : (
        <Reason value={reason} onChange={setReason} />
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} disabled={w.state.phase === 'done' || held !== undefined} submit={locked ? t('actions.lock.unlock') : t('actions.lock.lock')} onClose={onClose} />
    </form>
  );
}

/** "Khoá cập nhật" (clientUpdateProhibited): set by the platform after each contact change, removed or set here. */
function UpdateLockForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const locked = d.registry_statuses.some((s) => s.status === 'clientUpdateProhibited');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { locked: boolean; reason: string }, opts) => api.setDomainUpdateLock(d.domain.id, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ locked: !locked, reason }).then((r) => r && onChanged());
      }}
    >
      <p className="text-sm">{locked ? t('actions.updateLock.nowLocked') : t('actions.updateLock.nowUnlocked')}</p>
      <p className="text-xs text-muted-foreground">{t('actions.updateLock.hint')}</p>
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} disabled={w.state.phase === 'done'} submit={locked ? t('actions.updateLock.unlock') : t('actions.updateLock.lock')} onClose={onClose} />
    </form>
  );
}

/** "Khoá tên miền" (clientHold): the registry stops publishing the domain in DNS until it is released. */
function HoldForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const held = d.registry_statuses.some((s) => s.status === 'clientHold');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { locked: boolean; reason: string }, opts) => api.setDomainClientHold(d.domain.id, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ locked: !held, reason }).then((r) => r && onChanged());
      }}
    >
      <p className="text-sm">{held ? t('actions.hold.nowHeld') : t('actions.hold.nowFree')}</p>
      <p className="text-xs text-muted-foreground">{t('actions.hold.hint')}</p>
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} disabled={w.state.phase === 'done'} submit={held ? t('actions.hold.release') : t('actions.hold.hold')} onClose={onClose} danger={!held} />
    </form>
  );
}

const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** One role at the registry next to the platform's contact of that role. */
function RegistryContactCard({ row, ours }: { row: RegistryContactRow; ours: DomainContact | undefined }) {
  const { t } = useTranslation('domains');
  const c = row.contact;
  const x: ContactData | null | undefined = ours?.details;
  const theirAddress = c ? c.full_address ?? addressLine({ street: c.street, city: c.city ?? '', province: c.province, postal_code: c.postal_code, country_code: c.country_code ?? '' }) : null;
  const ourAddress = x ? x.vn_address?.full_address ?? (x.address ? addressLine(x.address) : null) : null;
  const rows: [string, string | null | undefined, string | null | undefined][] = [
    [t('actions.registryContacts.fields.name'), c?.name, x?.name],
    [t('actions.registryContacts.fields.organization'), c?.organization, x?.organization],
    [t('actions.registryContacts.fields.email'), c?.email, x?.email],
    [t('actions.registryContacts.fields.phone'), c?.voice, x?.phone],
    [t('actions.registryContacts.fields.fax'), c?.fax, x?.fax],
    [t('actions.registryContacts.fields.address'), theirAddress, ourAddress],
    [t('actions.registryContacts.fields.identity'), c?.identity?.masked, x?.identifiers.map((i) => i.hint).filter(Boolean).join(' · ') || null],
  ];
  return (
    <section className="grid gap-2 rounded-md border p-3" data-registry-contact={row.role}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <RoleBadge role={row.role} />
        <span className="font-mono">{row.handle ?? '—'}</span>
        {row.handle || row.our_handle ? (
          <span className={row.same_handle ? 'text-xs text-muted-foreground' : 'text-xs font-medium text-warning'}>
            {row.same_handle ? t('actions.registryContacts.sameHandle') : t('actions.registryContacts.otherHandle', { handle: row.our_handle ?? '—' })}
          </span>
        ) : null}
        {c?.sponsored_by_us === false ? <span className="text-xs font-medium text-warning">{t('actions.registryContacts.notOurs')}</span> : null}
      </div>
      {row.read !== 'FOUND' ? (
        <p className="text-xs text-muted-foreground">{t(`actions.registryContacts.reads.${row.read}`, { outcome: row.outcome ?? '' })}</p>
      ) : (
        <table className="w-full table-fixed text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="w-28 py-1 font-normal">{t('actions.registryContacts.field')}</th>
              <th className="py-1 font-normal">{t('actions.registryContacts.atRegistry')}</th>
              <th className="py-1 font-normal">{t('actions.registryContacts.inSystem')}</th>
            </tr>
          </thead>
          <tbody>
            {rows
              .filter(([, a, b]) => a || b)
              .map(([label, a, b]) => (
                <tr key={label} className={!x || same(a, b) ? undefined : 'bg-warning-soft'}>
                  <td className="py-1 align-top text-muted-foreground">{label}</td>
                  <td className="py-1 align-top break-words">{a || '—'}</td>
                  <td className="py-1 align-top break-words">{b || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * "Liên hệ tại registry": domain:info then contact:info of each contact, read now and only read,
 * next to the platform's current contacts (identity numbers masked; a field that differs is
 * highlighted).
 */
function RegistryContactsView({ d, onClose }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const theirs = useApi((signal) => api.getDomainRegistryContacts(d.domain.id, { signal }), [d.domain.id]);
  const ours = useApi((signal) => api.getDomainContacts(d.domain.id, { signal }), [d.domain.id]);
  if (theirs.status === 'loading') return <Loading rows={3} />;
  if (theirs.status === 'error') return <DelegationError error={theirs.error} onRetry={theirs.reload} onClose={onClose} />;
  const current = ours.status === 'success' ? ours.data.items.filter((c) => !c.valid_until) : [];
  return (
    <div className="grid gap-3">
      <p className="text-xs text-muted-foreground">
        {t('actions.registryContacts.hint')} {t('actions.registryContacts.readAt', { time: formatDateTime(theirs.data.read_at) })}
      </p>
      {theirs.data.contacts.map((r) => (
        <RegistryContactCard key={`${r.role}-${r.handle ?? ''}`} row={r} ours={current.find((c) => c.role === r.role)} />
      ))}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={theirs.reload}>
          <RefreshCw /> {t('actions.registryContacts.reload')}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('actions.close')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** The label of each template a staff e-mail may send (an expired verification sends the notice of the hold). */
const EMAIL_LABEL = {
  'contact.verify_email': 'verifyEmail',
  'contact.unverified_suspended': 'unverifiedSuspended',
  'contact.data_reminder': 'dataReminder',
  'domain.expiry_reminder': 'expiryReminder',
  'domain.expired': 'expiredNotice',
} as const;
const emailLabel = (k: string) => EMAIL_LABEL[k as keyof typeof EMAIL_LABEL] ?? null;

/** "Gửi email": a template chosen, its recipient and content previewed, then sent with a reason (audited, on the domain's history). */
function EmailForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const list = useApi((signal) => api.listDomainEmails(d.domain.id, { signal }), [d.domain.id]);
  const [key, setKey] = useState<StaffEmailKey | ''>('');
  const preview = useApi((signal) => (key ? api.previewDomainEmail(d.domain.id, { template_key: key }, { signal }) : Promise.resolve(null)), [d.domain.id, key]);
  const [reason, setReason] = useState('');
  const w = useWrite((b: { template_key: StaffEmailKey; reason: string }, opts) => api.sendDomainEmail(d.domain.id, b, opts));
  const items = list.status === 'success' ? list.data.items : [];
  const p = preview.status === 'success' ? preview.data : null;
  const sent = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (key) void w.run({ template_key: key, reason }).then((r) => r && onChanged());
      }}
    >
      <Field label={t('actions.email.template')}>
        <select className={selectClass} value={key} onChange={(e) => setKey(e.target.value as StaffEmailKey | '')} required>
          <option value="">{list.status === 'loading' ? t('actions.loading') : t('actions.choose')}</option>
          {items.map((i) => (
            <option key={i.template_key} value={i.template_key} disabled={Boolean(i.unavailable)}>
              {t(`actions.email.templates.${EMAIL_LABEL[i.template_key]}`)}
              {i.unavailable ? ` — ${t(`actions.email.unavailable.${i.unavailable}`)}` : ''}
            </option>
          ))}
        </select>
      </Field>
      {list.status === 'error' ? <WriteError error={list.error} /> : null}
      {preview.status === 'loading' && key ? <Loading rows={2} /> : null}
      {preview.status === 'error' ? <WriteError error={preview.error} /> : null}
      {p?.via === 'UPSTREAM_REGISTRAR' ? <Callout>{t('actions.email.upstream')}</Callout> : null}
      {p?.via === 'EMAIL' ? (
        <div className="grid gap-2 rounded-md border p-3 text-sm">
          <div>
            <span className="text-muted-foreground">{t('actions.email.to')}:</span> {p.recipient_name ? `${p.recipient_name} ` : ''}
            <span className="font-mono">&lt;{p.recipient_email}&gt;</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('actions.email.subject')}:</span> {p.subject}
          </div>
          {p.template_key !== key ? (
            <p className="text-xs text-muted-foreground">
              {t('actions.email.actual', { template: emailLabel(p.template_key) ? t(`actions.email.templates.${emailLabel(p.template_key) ?? 'verifyEmail'}`) : p.template_key })}
            </p>
          ) : null}
          <iframe title={t('actions.email.preview')} sandbox="" srcDoc={p.html} className="h-72 w-full rounded border bg-white" />
          {key === 'contact.verify_email' ? <p className="text-xs text-muted-foreground">{t('actions.email.linkNote')}</p> : null}
        </div>
      ) : null}
      {!sent ? <Reason value={reason} onChange={setReason} /> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {sent ? <Done>{sent.via === 'UPSTREAM_REGISTRAR' ? t('actions.email.sent', { to: sent.recipient_masked ?? '—' }) : t('actions.email.queued', { to: sent.recipient_masked ?? '—' })}</Done> : null}
      <Footer busy={w.busy} disabled={!key || !p || Boolean(sent)} submit={t('actions.email.submit')} onClose={onClose} />
    </form>
  );
}

/**
 * "Tạo registrant mới": the contact the registrant change sends to the registry for subject `to`
 * (its current version). The change creates it when it reaches the registry; created here first,
 * staff have its ID ahead, and the change sends this same contact.
 */
function RegistrantContactPanel({ d, to, reason }: { d: DomainDetail; to: string; reason: string }) {
  const { t } = useTranslation('domains');
  const state = useApi((signal) => api.getRegistrantContact(d.domain.id, to, { signal }), [d.domain.id, to]);
  const w = useWrite((b: { subject_id: string; reason: string }, opts) => api.createRegistrantContact(d.domain.id, b, opts));
  const c: RegistrantContact | null = w.state.phase === 'done' ? w.state.result : state.status === 'success' ? state.data : null;
  const held = c?.state === 'SUCCESS';
  return (
    <fieldset className="grid gap-2 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{t('actions.registrant.contactTitle')}</legend>
      <p className="text-xs text-muted-foreground">{t('actions.registrant.contactHint')}</p>
      {state.status === 'loading' ? <Loading rows={1} /> : null}
      {state.status === 'error' ? <WriteError error={state.error} /> : null}
      {c ? (
        <p className="text-sm">
          <span className="font-mono">{c.handle}</span> ·{' '}
          <span className={held ? 'text-success' : 'text-muted-foreground'}>{held ? t('actions.registrant.contactCreated') : t('actions.registrant.contactNotCreated')}</span>
        </p>
      ) : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {c && !held ? (
        <div>
          <Button type="button" size="sm" variant="outline" disabled={w.busy || !reason.trim()} onClick={() => void w.run({ subject_id: to, reason })}>
            {t('actions.registrant.createContact')}
          </Button>
          {!reason.trim() ? <span className="ml-2 text-xs text-muted-foreground">{t('actions.registrant.contactReason')}</span> : null}
        </div>
      ) : null}
    </fieldset>
  );
}

function TransferCodeForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.issueDomainTransferCode(d.domain.id, b, opts));
  const r: TransferCodeResult | null = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ reason }).then((x) => x && onChanged());
      }}
    >
      <Callout tone="warn">{t('actions.code.warning')}</Callout>
      {!r ? <Reason value={reason} onChange={setReason} /> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {r?.delivery === 'RETURNED' ? (
        <Field label={t('actions.code.code')} hint={t('actions.code.once')}>
          <Input readOnly value={r.auth_info} className="font-mono" onFocus={(e) => e.currentTarget.select()} />
        </Field>
      ) : null}
      {r?.delivery === 'SENT_TO_REGISTRANT' ? <Done>{t('actions.code.sent', { recipient: r.recipient ?? '—' })}</Done> : null}
      <Footer busy={w.busy} disabled={Boolean(r)} submit={t('actions.code.submit')} onClose={onClose} />
    </form>
  );
}

/**
 * Renewal on the owner's account. Within 24 hours of the last renewal (or while one runs) the quote
 * carries `renewal_block`: its reason and times are shown, and a staff member may renew once more
 * only by ticking the confirmation and giving the reason (owner decision 2026-10-07). A renewal
 * still running cannot be overridden.
 */
function RenewForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const [years, setYears] = useState(1);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const quote = useApi((signal) => api.quoteDomainRenewal(d.domain.id, { years }, { signal }), [d.domain.id, years]);
  const w = useWrite((b: { quote_id: string; reason: string; override_recent_renewal?: boolean }, opts) => api.renewDomain(d.domain.id, b, opts));
  const q: RenewQuote | null = quote.status === 'success' ? quote.data : null;
  const block = q?.renewal_block ?? null;
  // Refused unless confirmed (never while another renewal runs).
  const held = block !== null && (!block.overridable || !confirmed);
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (q && !held) void w.run({ quote_id: q.quote_id, reason, ...(block ? { override_recent_renewal: true } : {}) }).then((r) => r && onChanged());
      }}
    >
      <Field label={t('actions.renew.years')}>
        <select className={selectClass} value={years} onChange={(e) => setYears(Number(e.target.value))}>
          {[1, 2, 3, 4, 5, 10].map((y) => (
            <option key={y} value={y}>
              {t('actions.renew.yearsN', { count: y })}
            </option>
          ))}
        </select>
      </Field>
      {quote.status === 'loading' ? <Loading rows={1} /> : null}
      {quote.status === 'error' ? <WriteError error={quote.error} /> : null}
      {q ? <p className="text-sm">{t('actions.renew.price', { total: formatMoney(q.total as Parameters<typeof formatMoney>[0]), customer: d.domain.customer_name })}</p> : null}
      {block ? (
        <Callout tone="warn">
          <p>{block.message}</p>
          {block.overridable ? <p className="mt-1">{t('actions.renew.recent', { count: years })}</p> : null}
        </Callout>
      ) : null}
      {block?.overridable ? (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>
            {t('actions.renew.confirm')}
            <span className="block text-xs text-muted-foreground">{t('actions.renew.confirmHint')}</span>
          </span>
        </label>
      ) : null}
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <Done>{t('actions.renew.started')}</Done> : null}
      <Footer
        busy={w.busy}
        disabled={!q || held || w.state.phase === 'done'}
        submit={block ? t('actions.renew.submitAnyway') : t('actions.renew.submit')}
        onClose={onClose}
      />
    </form>
  );
}

/** Restore of a domain in its redemption period: the price on the owner's account, then the order
 * and the restore at the registry (the customer API's restore). */
function RestoreForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const [reason, setReason] = useState('');
  const quote = useApi((signal) => api.quoteDomainRestore(d.domain.id, { signal }), [d.domain.id]);
  const w = useWrite((b: { quote_id: string; reason: string }, opts) => api.restoreDomain(d.domain.id, b, opts));
  const q: RenewQuote | null = quote.status === 'success' ? quote.data : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (q) void w.run({ quote_id: q.quote_id, reason }).then((r) => r && onChanged());
      }}
    >
      {quote.status === 'loading' ? <Loading rows={1} /> : null}
      {quote.status === 'error' ? <WriteError error={quote.error} /> : null}
      {q ? <p className="text-sm">{t('actions.restore.price', { total: formatMoney(q.total as Parameters<typeof formatMoney>[0]), customer: d.domain.customer_name })}</p> : null}
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <Done>{t('actions.restore.started')}</Done> : null}
      <Footer busy={w.busy} disabled={!q || w.state.phase === 'done'} submit={t('actions.restore.submit')} onClose={onClose} />
    </form>
  );
}

function RegistrantForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const subjects = useApi((signal) => api.listSubjects({ customer_id: d.domain.customer_id, limit: 100 }, { signal }), [d.domain.customer_id]);
  const [to, setTo] = useState('');
  const [optOut, setOptOut] = useState(false);
  const [reason, setReason] = useState('');
  // As the server decides: Tino staff record consent by any method, anyone else (a customer
  // user) not at all — each registrant confirms with the link e-mailed to them.
  const mode = consentMode(useAccess().grants);
  const [method, setMethod] = useState<StaffConsentMethod>('SIGNED_DOCUMENT');
  const start = useWrite((b: { to_subject_id: string; transfer_lock_opt_out: boolean; reason: string }, opts) => api.startRegistrantChange(d.domain.id, b, opts));
  const confirm = useWrite((b: { changeId: string; side: ConsentSide }, opts) =>
    api.confirmRegistrantChange(b.changeId, { side: b.side, method, reason }, opts),
  );
  const started = start.state.phase === 'done' ? start.state.result : null;
  const change = started?.change_id ?? null;
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const others = subjects.status === 'success' ? subjects.data.items.filter((s) => s.id !== d.domain.current_subject_id) : [];
  const record = async (side: ConsentSide) => {
    if (!change) return;
    const r = await confirm.run({ changeId: change, side });
    if (r) {
      setConfirmed((c) => [...c, side]);
      onChanged();
    }
  };
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!change) void start.run({ to_subject_id: to, transfer_lock_opt_out: optOut, reason }).then((r) => r && onChanged());
      }}
    >
      {!change ? (
        <>
          <Field label={t('actions.registrant.to')} hint={t('actions.registrant.toHint')}>
            <select className={selectClass} value={to} onChange={(e) => setTo(e.target.value)} required>
              <option value="">{subjects.status === 'loading' ? t('actions.loading') : t('actions.choose')}</option>
              {others.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.legal_name ?? s.id}
                </option>
              ))}
            </select>
          </Field>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} /> {t('actions.registrant.optOut')}
          </label>
          <Reason value={reason} onChange={setReason} />
          {to ? <RegistrantContactPanel key={to} d={d} to={to} reason={reason} /> : null}
        </>
      ) : mode === 'EMAIL_LINK' ? (
        <Done>{t('actions.registrant.startedByEmail')}</Done>
      ) : (
        <>
          <Done>{t('actions.registrant.started')}</Done>
          <Field label={t('actions.registrant.method')}>
            <select className={selectClass} value={method} onChange={(e) => setMethod(e.target.value as StaffConsentMethod)}>
              {CONSENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`actions.registrant.methods.${m}`)}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex flex-wrap gap-2">
            {CONSENT_SIDES.map((side) => (
              <Button
                key={side}
                type="button"
                variant="outline"
                size="sm"
                disabled={confirm.busy || confirmed.includes(side)}
                onClick={() => void record(side)}
              >
                {confirmed.includes(side) ? '✓ ' : ''}
                {t(side === 'CURRENT_REGISTRANT' ? 'actions.registrant.confirmCurrentRegistrant' : 'actions.registrant.confirmNewRegistrant')}
              </Button>
            ))}
          </div>
          {confirmed.length === 2 ? <Done>{t('actions.registrant.authorized')}</Done> : null}
        </>
      )}
      {started?.registrant_handle ? <p className="text-sm">{t('actions.registrant.handleSent', { handle: started.registrant_handle })}</p> : null}
      {started?.name_change_id ? (
        <Callout tone="warn">
          {t('actions.registrant.nameChange')}{' '}
          <Link to={`/name-changes/${started.name_change_id}`} className="font-medium text-primary hover:underline">
            {t('actions.registrant.openNameChange')}
          </Link>
        </Callout>
      ) : null}
      {start.state.phase === 'error' ? <WriteError error={start.state.error} /> : null}
      {confirm.state.phase === 'error' ? <WriteError error={confirm.state.error} /> : null}
      <Footer busy={start.busy} disabled={Boolean(started) || !to} submit={t('actions.registrant.submit')} onClose={onClose} />
    </form>
  );
}

function DeleteForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('domains');
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.deleteDomain(d.domain.id, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ reason }).then((r) => r && onChanged());
      }}
    >
      <Callout tone="crit">{t('actions.delete.warning')}</Callout>
      <Field label={t('actions.delete.type', { name: d.domain.ascii_name })}>
        <Input value={typed} onChange={(e) => setTyped(e.target.value.trim().toLowerCase())} autoComplete="off" />
      </Field>
      <Reason value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <ResultText status={w.state.result.status} /> : null}
      <Footer busy={w.busy} disabled={typed !== d.domain.ascii_name || w.state.phase === 'done'} submit={t('actions.delete.submit')} onClose={onClose} danger />
    </form>
  );
}

const FORMS: Record<Action, (p: { d: DomainDetail; onClose: () => void; onChanged: () => void }) => ReactNode> = {
  nameservers: NameserversForm,
  dnssec: DnssecForm,
  lock: LockForm,
  updateLock: UpdateLockForm,
  hold: HoldForm,
  transferCode: TransferCodeForm,
  renew: RenewForm,
  restore: RestoreForm,
  registrant: RegistrantForm,
  nameChange: NameChangeForm,
  registryContacts: RegistryContactsView,
  email: EmailForm,
  delete: DeleteForm,
};

/** Where each "Đi tới" entry of the menu opens. */
function relatedLinks(d: DomainDetail): { id: string; to: string }[] {
  const x = d.domain;
  const q = (p: Record<string, string>) => new URLSearchParams(p).toString();
  return [
    { id: 'customer', to: `/customers/${encodeURIComponent(x.customer_id)}` },
    ...(x.current_subject_id ? [{ id: 'subject', to: `/subjects/${encodeURIComponent(x.current_subject_id)}` }] : []),
    { id: 'dossier', to: domainTabUrl(x.ascii_name, 'dossier') },
    { id: 'orders', to: domainTabUrl(x.ascii_name, 'finance', 'orders') },
    { id: 'invoices', to: domainTabUrl(x.ascii_name, 'finance', 'invoices') },
    { id: 'transfers', to: domainTabUrl(x.ascii_name, 'transfer', 'transfers') },
    { id: 'registryTx', to: `/ops?${q({ tab: 'registry', sub: 'transactions', object_key: x.ascii_name })}` },
    { id: 'work', to: `/?${q({ tab: 'queue', domain_id: x.id, status: 'OPEN' })}` },
    { id: 'history', to: domainTabUrl(x.ascii_name, 'history') },
  ];
}

const ACTIONS = Object.keys(FORMS) as Action[];

/** Which actions the domain's state allows. */
export function enabledActions(detail: DomainDetail): Record<Action, boolean> {
  const state = detail.domain.lifecycle_state;
  const active = state === 'ACTIVE' || state === 'GRACE_PERIOD';
  return {
    nameservers: active,
    dnssec: active,
    lock: state === 'ACTIVE',
    updateLock: active,
    // A hold for an unverified e-mail or an abuse case is lifted where it was placed (the server says so).
    hold: state === 'ACTIVE',
    transferCode: state === 'ACTIVE',
    renew: active,
    // Redemption: restored through the registry (the customer API's restore).
    restore: state === 'REDEMPTION',
    registrant: state === 'ACTIVE',
    // .vn: another legal name goes through VNNIC's panel (documents, review).
    nameChange: detail.domain.tld_class === 'VN' && state === 'ACTIVE',
    // The registry still holds the domain.
    registryContacts: active || state === 'EXPIRED' || state === 'REDEMPTION' || state === 'PENDING_DELETE',
    email: active || state === 'EXPIRED' || state === 'REDEMPTION',
    delete: active,
  };
}

/**
 * "Thao tác tên miền" as a quick-action row (owner request 2026-10-07, after HostBill's domain
 * buttons): renewal first, then the everyday changes as buttons — Máy chủ tên, Khoá / Mở khoá
 * chuyển, Khoá / Mở khoá tên miền (clientHold), Mã chuyển, Đồng bộ registry (with
 * `registry.transaction.read`), Gửi email — every other action and every related record under
 * "Thêm". The open dialog is in the URL (`?action=renew`), so the Ctrl-K menu, the attention
 * banner and the tabs open the same dialog. `onChanged` reloads the page data. `renewBlocked`
 * says why renewal is off (shown under the row).
 */
export function DomainActions({ detail, onChanged, renewBlocked }: { detail: DomainDetail; onChanged: () => void; renewBlocked?: string | null }) {
  const { t } = useTranslation('domains');
  const { t: tu } = useTranslation('ux');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canSync = useAccess().has(['registry.transaction.read']);
  const sync = useWrite((_: null, opts) => api.syncDomainRegistry(detail.domain.id, opts));
  const enabled = enabledActions(detail);
  const requested = params.get('action') as Action | null;
  const open = requested && (ACTIONS as string[]).includes(requested) && enabled[requested] ? requested : null;
  const setOpen = (a: Action | null) => {
    const p = new URLSearchParams(params);
    if (a) p.set('action', a);
    else p.delete('action');
    setParams(p, { replace: true, preventScrollReset: true });
  };
  // Reload the page only when the dialog closes: reloading at once would unmount the dialog
  // before the result (e.g. a new transfer code) is read.
  const [changed, setChanged] = useState(false);
  const close = () => {
    setOpen(null);
    if (changed) {
      setChanged(false);
      onChanged();
    }
  };
  const locked = detail.registry_statuses.some((s) => s.status === 'clientTransferProhibited');
  const held = detail.registry_statuses.some((s) => s.status === 'clientHold');
  const more: Action[] = ['restore', ...(detail.domain.tld_class === 'VN' ? (['nameChange'] as const) : [])];
  const Form = open ? FORMS[open] : null;
  // The right column (owner 2026-10-07): renewal first, the frequent actions as full-width buttons.
  const rail = 'w-full justify-start';
  return (
    <>
      <div className="grid gap-2" data-action-bar aria-label={t('actions.quick.title')}>
        <Button type="button" className="w-full" disabled={!enabled.renew} onClick={() => setOpen('renew')}>
          <CalendarPlus /> {tu('domain.primary.renew')}
        </Button>
        {renewBlocked ? <p className="text-xs leading-relaxed text-warning">{renewBlocked}</p> : null}
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.nameservers} onClick={() => setOpen('nameservers')}>
          <Server /> {tu('domain.primary.nameservers')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.lock} onClick={() => setOpen('lock')}>
          {locked ? <LockOpen /> : <Lock />} {locked ? tu('domain.primary.unlock') : tu('domain.primary.lock')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.hold} onClick={() => setOpen('hold')}>
          <Ban /> {held ? t('actions.hold.release') : t('actions.hold.hold')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.transferCode} onClick={() => setOpen('transferCode')}>
          <KeyRound /> {t('actions.items.transferCode')}
        </Button>
        {canSync ? (
          <Button type="button" size="sm" variant="outline" className={rail} disabled={sync.busy} onClick={() => void sync.run(null).then((r) => r && onChanged())}>
            <RefreshCw className={sync.busy ? 'animate-spin' : undefined} /> {sync.busy ? t('actions.quick.syncBusy') : t('actions.quick.sync')}
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.email} onClick={() => setOpen('email')}>
          <Mail /> {t('actions.items.email')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.registryContacts} onClick={() => setOpen('registryContacts')}>
          <Contact /> {t('actions.items.registryContacts')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.registrant} onClick={() => setOpen('registrant')}>
          <UserCog /> {t('actions.items.registrant')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.dnssec} onClick={() => setOpen('dnssec')}>
          <ShieldCheck /> {t('actions.items.dnssec')}
        </Button>
        <Button type="button" size="sm" variant="outline" className={rail} disabled={!enabled.updateLock} onClick={() => setOpen('updateLock')}>
          <LockKeyhole /> {t('actions.items.updateLock')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className={rail}>
              <MoreHorizontal /> {tu('actionBar.more')} <ChevronDown className="ml-auto size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
            {more.map((id) => (
              <DropdownMenuItem key={id} disabled={!enabled[id]} onSelect={() => setOpen(id)}>
                {t(`actions.items.${id}`)}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onSelect={() => void navigate(domainTabUrl(detail.domain.ascii_name, 'addons'))}>{t('actions.items.services')}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onChanged}>
              <RefreshCw /> {t('detail.refresh')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" disabled={!enabled.delete} onSelect={() => setOpen('delete')}>
              {t('actions.items.delete')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">{tu('actionBar.related')}</DropdownMenuLabel>
            {relatedLinks(detail).map((r) => (
              <DropdownMenuItem key={r.id} asChild>
                <Link to={r.to}>{t(`actions.related.${r.id}` as 'actions.related.customer')}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {sync.state.phase === 'error' ? (
        <div className="mt-2">
          <WriteError error={sync.state.error} />
        </div>
      ) : sync.state.phase === 'done' ? (
        <p role="status" className="mt-2 text-xs text-muted-foreground">
          {t('actions.quick.synced', { time: formatDateTime(sync.state.result.synced_at) })}
        </p>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{open ? `${t(`actions.items.${open}`)} · ${detail.domain.unicode_name}` : ''}</DialogTitle>
            <DialogDescription>{open === 'registryContacts' || open === 'email' ? t(`actions.descriptions.${open}`) : t('actions.description')}</DialogDescription>
          </DialogHeader>
          {Form ? (
            <NotifyCustomerChoice>
              <Form d={detail} onClose={close} onChanged={() => setChanged(true)} />
            </NotifyCustomerChoice>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
